// Die Befehlspalette (ST-UX-001, Bauvorgabe Slice 3): Kontakt oder Befehl von
// jeder Seite aus ohne Maus. Muster Combobox mit Listbox (APG): der Fokus
// bleibt im Feld, aria-activedescendant zeigt auf die aktive Option.
//
// Die Palette fuehrt nichts selbst aus. Sie meldet den gewaehlten Befehl an
// den Rahmen (`onBefehl`); der kennt Navigation, Suchlauf und Uebersicht.

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Button, Caption1, mergeClasses, Dialog, DialogBody, DialogSurface, Input, makeStyles, tokens } from '@fluentui/react-components';

import { plainMessageFor, type PlainMessage } from '../access.ts';
import {
  fetchRecentlyEditedParties,
  SEARCH_FIELD_HINT,
  searchParties,
  type PartySummary,
} from '../parties.ts';
import { abrufartFuer, eintraegeFuer, type Abruf, type Befehl, type Eintrag } from './befehlspalette-eintraege.ts';
import { Tasten } from './taste.tsx';
import { PALETTE_ID_FELD } from './tastenkuerzel.ts';
import { TypSymbol } from './typ-symbol.tsx';
import { useCombobox } from './use-combobox.ts';
import { useEntprellterAbruf } from './use-entprellter-abruf.ts';
import { VorgangNach } from './zustand.tsx';

/** So lange wartet der Abruf nach dem letzten Tastendruck. */
const VERZOEGERUNG_MS = 250;
const LISTE_ID = 'befehlspalette-liste';
/** Das Kreuz des Schliessen-Knopfs; nur Zierde, der Name steht am Knopf. */
const KREUZ = (
  <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);
const KEINE: readonly PartySummary[] = [];
const ANSAGE_ID = 'befehlspalette-ansage';

const useStyles = makeStyles({
  flaeche: {
    padding: 0,
    maxWidth: '40rem',
    width: 'calc(100vw - 1rem)',
    maxHeight: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    // Am Handy die ganze Flaeche wie die Suche einer App: Feld oben, Treffer
    // darunter, kein schwebendes Fenster mit Rand. Oben verankert, weil die
    // Bildschirmtastatur die untere Haelfte verdeckt.
    '@media (max-width: 640px)': {
      margin: 0,
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      borderRadius: 0,
      boxShadow: 'none',
    },
  },
  koerper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    gap: 0,
    maxHeight: '80vh',
    '@media (max-width: 640px)': { maxHeight: 'none', height: '100%' },
  },
  feldZeile: { padding: '0.75rem', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, display: 'flex', alignItems: 'center', gap: '0.5rem' },
  // Am Handy fehlt Esc: ein Schliessen-X mit 44 px (M-03); breiter genuegt Esc.
  schliessen: {
    width: '44px',
    minWidth: '44px',
    maxWidth: 'none',
    height: '44px',
    flexShrink: 0,
    // Fehlt nur mit feinem Zeiger in der Breite: ein Tablet ohne Tastatur hat kein Esc (DESIGN P3 zu #557).
    '@media (min-width: 641px) and (pointer: fine)': { display: 'none' },
  },
  // Ohne Tastatur sagen die Tastenhinweise nichts (M-03).
  nurMitTastatur: { '@media (pointer: coarse)': { display: 'none' } },
  feld: { width: '100%', flexGrow: 1, minWidth: 0 },
  liste: { overflowY: 'auto', padding: '0.5rem', minHeight: 0, flexGrow: 1 },
  gruppeKopf: {
    display: 'block',
    margin: '0.5rem 0.5rem 0.25rem',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    // Die aktive Option traegt Flaeche, Gewicht UND Balken, nie nur Farbe
    // (ADR-0002, WCAG 1.4.1) -- wie der aktuelle Eintrag der Navigation.
    '&[aria-selected="true"]': {
      backgroundColor: tokens.colorBrandBackground2,
      color: tokens.colorBrandForeground2,
      fontWeight: tokens.fontWeightSemibold,
      boxShadow: `inset 3px 0 0 ${tokens.colorBrandStroke1}`,
    },
  },
  text: { display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 },
  titel: { overflowWrap: 'anywhere' },
  zweitzeile: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere', fontWeight: tokens.fontWeightRegular },
  status: { padding: '0.5rem 1rem', color: tokens.colorNeutralForeground1, overflowWrap: 'anywhere' },
  suchzeile: { display: 'block', minHeight: tokens.lineHeightBase200 },
  fuss: {
    padding: '0.5rem 1rem',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    overflowWrap: 'anywhere',
  },
  // v4: eine Zeile, die Tasten als Kappen.
  tastenhilfe: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.75rem' },
  hilfe: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem' },
});

export interface BefehlspaletteProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly darfAnlegen: boolean;
  readonly darfKontakte: boolean;
  readonly darfWiedervorlagen: boolean;
  readonly notizLabel: string | undefined;
  /** Schliesst ohne Befehl; der Rahmen gibt den Fokus zurueck. */
  readonly onSchliessen: () => void;
  /** Schliesst und fuehrt aus. */
  readonly onBefehl: (befehl: Befehl) => void;
}

type Suchantwort =
  | { readonly art: 'treffer'; readonly parties: readonly PartySummary[] }
  | { readonly art: 'gestoert'; readonly stoerung: PlainMessage }
  | { readonly art: 'abgemeldet' };

function optionsName(eintrag: Eintrag): string {
  // Erst was man sieht, in der Reihenfolge des Bildes, dann die Art: so steht die sichtbare
  // Beschriftung zusammenhaengend im Namen (WCAG 2.5.3).
  const teile = [eintrag.titel];
  if (eintrag.zweitzeile !== undefined) {
    teile.push(eintrag.zweitzeile);
  }
  if (eintrag.artLabel !== undefined && eintrag.artLabel !== eintrag.zweitzeile) {
    teile.push(eintrag.artLabel);
  }
  return teile.join(', ');
}

/** Die Ueberschrift als Satz: Zeile und Ansage trennen sie per Punkt vom Schritt, ein vorhandenes Satzzeichen bleibt. */
function alsSatz(headline: string): string {
  return /[.?!]$/u.test(headline) ? headline : `${headline}.`;
}

function ansageFuer(suchtModus: boolean, abruf: Abruf, anzahl: number, stoerung: PlainMessage | undefined): string {
  if (suchtModus && abruf === 'gestoert' && stoerung !== undefined) {
    return `${alsSatz(stoerung.headline)} ${stoerung.nextStep}`;
  }
  if (!suchtModus || abruf !== 'geladen') {
    return '';
  }
  return anzahl === 0 ? 'Kein Treffer' : anzahl === 1 ? '1 Kontakt' : `${anzahl} Kontakte`;
}

export function Befehlspalette(props: BefehlspaletteProps): ReactElement {
  const styles = useStyles();
  const [begriff, setBegriff] = useState('');
  const [zuletzt, setZuletzt] = useState<readonly PartySummary[]>([]);
  const [wiederholung, setWiederholung] = useState(0);
  const { apiBaseUrl, accessToken, onReauthenticate, darfKontakte } = props;

  const abrufart = abrufartFuer(begriff, darfKontakte);
  const suchterm = abrufart.art === 'name-oder-kennung' ? abrufart.term : undefined;
  const sucheAbrufen = useCallback(
    async (term: string, signal: AbortSignal): Promise<Suchantwort> => {
      try {
        const token = await accessToken();
        if (signal.aborted) {
          return { art: 'abgemeldet' };
        }
        if (token === undefined) {
          onReauthenticate();
          return { art: 'abgemeldet' };
        }
        const antwort = await searchParties({ baseUrl: apiBaseUrl, accessToken: token, term, signal });
        return antwort.outcome === 'loaded' ? { art: 'treffer', parties: antwort.parties } : { art: 'gestoert', stoerung: antwort.message };
      } catch {
        // Kein Netz oder keine Antwort: derselbe Satz wie auf den Lesebildschirmen.
        return { art: 'gestoert', stoerung: plainMessageFor(undefined, { status: 0, answered: false, hasCorrelationId: false }) };
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate],
  );
  const stand = useEntprellterAbruf({ anfrage: suchterm, verzoegerungMs: VERZOEGERUNG_MS, abrufen: sucheAbrufen, wiederholung });
  const antwort = stand.zustand === 'fertig' || stand.zustand === 'laedt' ? stand.antwort : undefined;
  const abruf: Abruf =
    stand.zustand === 'laedt' ? 'laedt' : antwort?.art === 'treffer' ? 'geladen' : antwort?.art === 'gestoert' ? 'gestoert' : 'ruht';
  const treffer = antwort?.art === 'treffer' ? antwort.parties : KEINE;
  const stoerung = antwort?.art === 'gestoert' ? antwort.stoerung : undefined;

  const ergebnis = useMemo(
    () =>
      eintraegeFuer({
        begriff,
        zuletzt,
        treffer,
        abruf,
        darfAnlegen: props.darfAnlegen,
        darfKontakte,
        darfWiedervorlagen: props.darfWiedervorlagen,
        notizLabel: props.notizLabel,
      }),
    [begriff, zuletzt, treffer, abruf, props.darfAnlegen, darfKontakte, props.darfWiedervorlagen, props.notizLabel],
  );

  useEffect(() => {
    if (!darfKontakte) {
      return undefined;
    }
    let verworfen = false;
    void (async () => {
      const token = await accessToken();
      if (token === undefined || verworfen) {
        return;
      }
      const antwort = await fetchRecentlyEditedParties({ baseUrl: apiBaseUrl, accessToken: token }).catch(() => undefined);
      if (!verworfen && antwort?.outcome === 'loaded') {
        setZuletzt(antwort.parties);
      }
    })();
    return () => {
      verworfen = true;
    };
  }, [accessToken, apiBaseUrl, darfKontakte]);

  const waehlen = (eintrag: Eintrag): void => {
    if (eintrag.befehl.art === 'erneut') {
      setWiederholung((zahl) => zahl + 1);
      return;
    }
    props.onBefehl(eintrag.befehl);
  };

  const combobox = useCombobox({
    optionen: ergebnis.optionen,
    schluessel: (eintrag) => eintrag.id,
    idPraefix: 'befehlspalette-',
    listId: LISTE_ID,
    offen: true,
    ersteIstAktiv: true,
    onWaehlen: waehlen,
  });

  const reduziert = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Ohne Tastatur bleibt vom Fuss nur, was er gerade sagt; leer faellt er ganz weg (DESIGN P3 zu #557).
  const fussHatInhalt = (ergebnis.suchtModus && abruf === 'laedt') || begriff.trim().length < 3;
  const ansage = ansageFuer(ergebnis.suchtModus, abruf, treffer.length, stoerung);

  return (
    <Dialog open modalType="modal" onOpenChange={(_, daten) => { if (!daten.open) props.onSchliessen(); }}>
      <DialogSurface aria-label="Schnellzugriff" data-befehlspalette="" className={styles.flaeche} {...(reduziert ? { surfaceMotion: null } : {})}>
        <DialogBody className={styles.koerper}>
          <div className={styles.feldZeile}>
            <Input
              id={PALETTE_ID_FELD}
              className={styles.feld}
              value={begriff}
              onChange={(_, daten) => {
                setBegriff(daten.value);
                combobox.zuruecksetzen();
              }}
              onKeyDown={combobox.beiTaste}
              placeholder="Name, E-Mail, Kennung oder Befehl"
              autoComplete="off"
              autoFocus
              input={{
                ...combobox.eingabeAttribute,
                'aria-label': 'Kontakt oder Befehl',
              }}
            />
            {/* Nach dem Feld, im Bild wie im DOM (iOS-Muster: Feld links, Schliessen rechts):
                der Dialog fokussiert sein erstes Bedienelement noch in der oeffnenden Geste,
                und nur dann zeigt iOS die Tastatur. */}
            <Button appearance="subtle" className={styles.schliessen} aria-label="Schließen" icon={KREUZ} onClick={props.onSchliessen} />
          </div>

          {stoerung !== undefined && abruf === 'gestoert' ? (
            <div className={styles.status}>
              <strong>{alsSatz(stoerung.headline)}</strong> {stoerung.nextStep}
            </div>
          ) : undefined}
          {stoerung?.correlationId !== undefined && abruf === 'gestoert' ? (
            // Derselbe Innenabstand wie der Satz darueber; sonst klebt die Zeile schmal am Rand.
            <div className={styles.status}>
              <VorgangNach meldungen={[stoerung]} />
            </div>
          ) : undefined}
          {ergebnis.keinTreffer ? <div className={styles.status}>Kein Treffer für „{begriff.trim()}“</div> : undefined}

          <div id={LISTE_ID} role="listbox" aria-label="Ergebnisse" className={styles.liste}>
            {ergebnis.gruppen.map((gruppe, nummer) => {
              const kopfId = `befehlspalette-gruppe-${nummer}`;
              return (
                <div key={`${gruppe.id}-${nummer}`} role="group" {...(gruppe.label === undefined ? {} : { 'aria-labelledby': kopfId })}>
                  {gruppe.label === undefined ? undefined : (
                    <span id={kopfId} className={styles.gruppeKopf}>
                      {gruppe.label}
                    </span>
                  )}
                  {gruppe.eintraege.map((eintrag) => (
                    <div
                      key={eintrag.id}
                      {...combobox.optionAttribute(eintrag)}
                      aria-label={optionsName(eintrag)}
                      // Kein aria-keyshortcuts: bei offener Palette steht der Fokus im Feld, und dort wirken
                      // Einzeltasten nicht (kuerzelFuer). Die Tasten nennt die Kuerzeluebersicht als Text.
                      className={styles.option}
                    >
                      {eintrag.organisation === undefined ? undefined : <TypSymbol organisation={eintrag.organisation} groesse={20} />}
                      <span className={styles.text}>
                        <span className={styles.titel}>{eintrag.titel}</span>
                        {/* Das Leerzeichen trennt die Woerter im Text (axe liest sonst "MüllerVerlag"), im Flex-Layout ist es unsichtbar. */}
                        {eintrag.zweitzeile === undefined ? undefined : <> <span className={styles.zweitzeile}>{eintrag.zweitzeile}</span></>}
                      </span>
                      {eintrag.taste === undefined ? undefined : (
                        <span className={styles.nurMitTastatur}>
                          <Tasten folge={eintrag.taste} stumm />
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          <div id={ANSAGE_ID} role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
            {ansage}
          </div>

          <div className={mergeClasses(styles.fuss, !fussHatInhalt && styles.nurMitTastatur)} data-palette-fuss="">
            {/* Die Zeile ist immer da: erschiene "Suche läuft …" nur zeitweise
                über der Liste, spränge die Liste bei jedem Tastendruck. */}
            <Caption1 className={styles.suchzeile}>{ergebnis.suchtModus && abruf === 'laedt' ? 'Suche läuft …' : ''}</Caption1>
            <Caption1 className={mergeClasses(styles.tastenhilfe, styles.nurMitTastatur)}>
              <span className={styles.hilfe}>
                <Tasten folge="↑ ↓" /> wählen
              </span>
              <span className={styles.hilfe}>
                <Tasten folge="Enter" /> öffnen
              </span>
              <span className={styles.hilfe}>
                <Tasten folge="Esc" /> schließen
              </span>
            </Caption1>
            {/* Die Regel der Suche nur, solange sie noch greift: ab drei Zeichen
                sucht die Palette, und der Satz waere eine zweite Zeile ohne Nutzen (v4). */}
            {begriff.trim().length < 3 ? <Caption1>{SEARCH_FIELD_HINT}</Caption1> : undefined}
          </div>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
